// src/pages/CreateGoldScheme.jsx
import React, { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { FaArrowLeft } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useAddGoldSchemeMutation } from "../toolkitServices/goldSchemeApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";

// ---------- Shared UI tokens ----------
const baseInput =
  "w-full px-3 py-2 border rounded-lg text-[13px] md:text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const DEFAULTS = {
  schemeName: "",
  schemeType: "AMOUNT",
  branchId: "",
  metalType: "",

  installmentMonths: "",
  benefitMonths: "",
  maturityMonths: "",
  schemeAmountPerMonth: "",
  schemeCreationDate: new Date().toISOString().slice(0, 10),
  description: "",
};

const STEP_FIELDS = {
  1: ["schemeName", "schemeType", "branchId", "metalType"],
  2: [
    "installmentMonths",
    "benefitMonths",
    "maturityMonths",
    "schemeAmountPerMonth",
    "schemeCreationDate",
    "description",
  ],
};

const CreateGoldScheme = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [addGoldScheme, { isLoading, error, isSuccess, data }] =
    useAddGoldSchemeMutation();

  const {
    data: branchesRes,
    isLoading: isBranchesLoading,
    error: branchesError,
  } = useGetAllBranchesQuery({
    page: 1,
    limit: 1000,
    search: "",
    active: true,
  });

  const branches = Array.isArray(branchesRes?.data || branchesRes?.items)
    ? branchesRes.data || branchesRes.items
    : branchesRes || [];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setFocus,
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: DEFAULTS,
    mode: "onBlur",
    shouldUnregister: false,
  });

  const schemeType = watch("schemeType");
  const metalType = watch("metalType");
  const installmentMonths = watch("installmentMonths");
  const benefitMonths = watch("benefitMonths");

  useEffect(() => {
    const id = requestAnimationFrame(() => setFocus("schemeName"));
    return () => cancelAnimationFrame(id);
  }, [setFocus]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (step === 1) setFocus("schemeName");
      if (step === 2) setFocus("installmentMonths");
    });
    return () => cancelAnimationFrame(id);
  }, [step, setFocus]);

  useEffect(() => {
    const inst = Number(installmentMonths) || 0;
    const ben = Number(benefitMonths) || 0;
    const total = inst + ben;
    setValue("maturityMonths", total > 0 ? String(total) : "");
  }, [installmentMonths, benefitMonths, setValue]);

  useEffect(() => {
    if (schemeType === "GRAM") {
      if (!metalType) setValue("metalType", "GOLD");
    } else {
      setValue("metalType", "");
    }
  }, [schemeType, setValue, metalType]);

  useEffect(() => {
    if (!isSuccess) return;

    toast.success(data?.message || "Gold scheme created successfully!");

    reset({
      ...DEFAULTS,
      schemeCreationDate: new Date().toISOString().slice(0, 10),
    });

    setStep(1);
    setTimeout(() => setFocus("schemeName"), 0);
  }, [isSuccess, data, reset, setFocus]);

  useEffect(() => {
    if (error) {
      const msg =
        error?.data?.msg ||
        error?.data?.message ||
        error?.error ||
        "Error creating gold scheme!";
      toast.error(msg);
    }
  }, [error]);

  useEffect(() => {
    if (branchesError) toast.error("Failed to load branches");
  }, [branchesError]);

  const nextStep = useCallback(async () => {
    const list =
      step === 1
        ? ["schemeName", "schemeType", "branchId"].concat(
            schemeType === "GRAM" ? ["metalType"] : []
          )
        : STEP_FIELDS[2];

    const ok = await trigger(list, { shouldFocus: true });
    if (ok) setStep(2);
  }, [step, trigger, schemeType]);

  const prevStep = useCallback(() => setStep(1), []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";

      if (isSave) {
        e.preventDefault();
        if (step === 1) nextStep();
        else handleSubmit(onSubmit)();
      }

      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [handleSubmit, navigate, step, nextStep]);

  const onSubmit = async (form) => {
    const type = form.schemeType;

    const payload = {
      schemeName: (form.schemeName || "").trim(),
      branchId: form.branchId || null,
      schemeType: type,
      metalType: type === "GRAM" ? (form.metalType || "").trim() : null,

      installmentMonths: Number(form.installmentMonths) || 0,
      benefitsInMonths: Number(form.benefitMonths) || 0,

      schemeAmountPerMonth: Number(form.schemeAmountPerMonth) || 0,

      schemeCreationDate: form.schemeCreationDate || null,
      description: (form.description || "").trim(),
    };

    try {
      await addGoldScheme(payload).unwrap();
    } catch {
      // toast handled in error effect
    }
  };

  const loading = isLoading || isSubmitting;

  const goToPlan = async () => {
    const ok = await trigger(
      ["schemeName", "schemeType", "branchId"].concat(
        schemeType === "GRAM" ? ["metalType"] : []
      ),
      { shouldFocus: true }
    );

    if (ok) setStep(2);
  };

  return (
    <div className="w-full px-2 md:px-0">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-3 py-2 flex items-center gap-3">
          <Link
            to="/scheme/gold-scheme"
            className="inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Gold Scheme List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-lg md:text-xl font-semibold text-[#4b3d91] leading-tight truncate">
                  Create Gold Scheme
                </h1>

                <p className="hidden md:block text-xs text-gray-500 mt-0.5">
                  Step {step} of 2 •{" "}
                  <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">S</kbd>{" "}
                  {step === 1 ? "next" : "save"}
                </p>
              </div>

              <div className="hidden sm:flex items-center gap-2">
                <span
                  className={`text-xs px-2.5 py-1 rounded-full border ${
                    step === 1
                      ? "bg-[#9481ce]/10 border-[#9481ce]/30 text-[#4b3d91]"
                      : "bg-gray-50 border-gray-200 text-gray-500"
                  }`}
                >
                  1. Basic
                </span>

                <span
                  className={`text-xs px-2.5 py-1 rounded-full border ${
                    step === 2
                      ? "bg-[#9481ce]/10 border-[#9481ce]/30 text-[#4b3d91]"
                      : "bg-gray-50 border-gray-200 text-gray-500"
                  }`}
                >
                  2. Plan
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-3 border border-gray-200 relative overflow-hidden"
      >
        {(loading || isBranchesLoading) && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        {/* Compact Step Tabs */}
        <div className="px-4 pt-4">
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-50 p-1 border border-gray-100">
            <button
              type="button"
              onClick={() => setStep(1)}
              className={`py-1.5 rounded-lg text-sm font-medium transition ${
                step === 1
                  ? "bg-[#9481ce] text-white shadow-sm"
                  : "text-gray-700 hover:bg-white"
              }`}
            >
              Basic
            </button>

            <button
              type="button"
              onClick={goToPlan}
              className={`py-1.5 rounded-lg text-sm font-medium transition ${
                step === 2
                  ? "bg-[#9481ce] text-white shadow-sm"
                  : "text-gray-700 hover:bg-white"
              }`}
            >
              Plan
            </button>
          </div>
        </div>

        <form className="p-4 md:p-5" onSubmit={handleSubmit(onSubmit)}>
          {/* STEP 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {/* Scheme Name */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Scheme Name<span className="text-red-500">*</span>
                </label>

                <input
                  {...register("schemeName", { required: true, minLength: 2 })}
                  placeholder="e.g. Diwali Dhamaka Scheme"
                  className={`${baseInput} ${
                    errors.schemeName ? errRing : okRing
                  }`}
                  autoComplete="off"
                />

                {errors.schemeName && (
                  <p className="mt-1 text-xs text-red-500">
                    Scheme name is required.
                  </p>
                )}
              </div>

              {/* Scheme Type */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Scheme Type<span className="text-red-500">*</span>
                </label>

                <select
                  {...register("schemeType", { required: true })}
                  className={`${baseInput} ${okRing} bg-white`}
                >
                  <option value="AMOUNT">Amount based</option>
                  {/* <option value="GRAM">Metal based</option> */}
                </select>
              </div>

              {/* Branch */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Branch<span className="text-red-500">*</span>
                </label>

                <select
                  {...register("branchId", { required: true })}
                  aria-invalid={!!errors.branchId}
                  className={`${baseInput} ${
                    errors.branchId ? errRing : okRing
                  } bg-white`}
                  disabled={isBranchesLoading}
                >
                  <option value="">Select branch</option>

                  {branches?.map((b) => (
                    <option key={b._id || b.id} value={b._id || b.id}>
                      {b.name || b.branchName || "Branch"}{" "}
                      {b.code ? `(${b.code})` : ""}
                    </option>
                  ))}
                </select>

                {errors.branchId && (
                  <p className="mt-1 text-xs text-red-500">
                    Branch is required.
                  </p>
                )}
              </div>

              {/* Metal Type */}
              {schemeType === "GRAM" && (
                <div className="md:col-span-2">
                  <label className="block text-[13px] font-medium text-gray-700 mb-1">
                    Metal Type<span className="text-red-500">*</span>
                  </label>

                  <select
                    {...register("metalType", {
                      required: schemeType === "GRAM",
                    })}
                    aria-invalid={!!errors.metalType}
                    className={`${baseInput} ${
                      errors.metalType ? errRing : okRing
                    } bg-white`}
                  >
                    <option value="">Select metal</option>
                    <option value="GOLD">Gold</option>
                    <option value="SILVER">Silver</option>
                  </select>

                  {errors.metalType && (
                    <p className="mt-1 text-xs text-red-500">
                      Metal type is required.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {/* Installment Months */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Installment Months<span className="text-red-500">*</span>
                </label>

                <input
                  type="number"
                  min={1}
                  {...register("installmentMonths", {
                    required: true,
                    min: 1,
                  })}
                  placeholder="e.g. 12"
                  className={`${baseInput} ${
                    errors.installmentMonths ? errRing : okRing
                  }`}
                />

                {errors.installmentMonths && (
                  <p className="mt-1 text-xs text-red-500">
                    Installment months is required.
                  </p>
                )}
              </div>

              {/* Benefit Months */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Benefit Months
                </label>

                <input
                  type="number"
                  min={0}
                  {...register("benefitMonths", { min: 0 })}
                  placeholder="e.g. 1"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Maturity Months */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Maturity Months
                </label>

                <input
                  type="number"
                  {...register("maturityMonths")}
                  readOnly
                  placeholder="Auto"
                  className={`${baseInput} bg-gray-50 cursor-not-allowed ${okRing}`}
                />
              </div>

              {/* Min Amount per Month */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Min Amount / Month ₹<span className="text-red-500">*</span>
                </label>

                <input
                  type="number"
                  min={1}
                  step="0.01"
                  {...register("schemeAmountPerMonth", {
                    required: true,
                    min: 1,
                  })}
                  placeholder="e.g. 5000"
                  className={`${baseInput} ${
                    errors.schemeAmountPerMonth ? errRing : okRing
                  }`}
                />

                {errors.schemeAmountPerMonth && (
                  <p className="mt-1 text-xs text-red-500">
                    Min amount is required.
                  </p>
                )}
              </div>

              {/* Scheme Creation Date */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Creation Date
                </label>

                <input
                  type="date"
                  {...register("schemeCreationDate")}
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Empty space for better desktop balance */}
              <div className="hidden lg:block" />

              {/* Notes */}
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Notes
                </label>

                <textarea
                  {...register("description")}
                  placeholder="Optional notes for internal reference"
                  rows={1}
                  className={`${baseInput} ${okRing} resize-none min-h-[42px]`}
                />
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-4 mt-4 border-t border-gray-100">
            {step === 2 && (
              <motion.button
                whileHover={{ scale: loading ? 1 : 1.01 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                type="button"
                onClick={prevStep}
                className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
                disabled={loading}
              >
                Previous
              </motion.button>
            )}

            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step === 1) nextStep();
                else handleSubmit(onSubmit)();
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-5 py-2 rounded-lg text-sm font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={loading || isBranchesLoading}
              title={step === 1 ? "Next (Ctrl+S)" : "Submit (Ctrl+S)"}
            >
              {loading ? "Saving..." : step === 1 ? "Next" : "Submit"}
            </motion.button>

            <Link
              to="/scheme/gold-scheme"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateGoldScheme;